import {useState} from "react";

import styled, {keyframes} from "styled-components";

import {MediaItem, MediaKind, MediaType} from "../../services/menu/models";
import MediaFrame from "./mediaFrame";
import noImage from "../../assets/noImageProduct.png"
import AwesomeSlider from 'react-awesome-slider';
import 'react-awesome-slider/dist/styles.css';

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${(props: { height?: number | string }) => props.height || 'calc(50vw - 15px)'};
  max-height: 200px;
  @media (min-width: 700px) and (max-width: 895px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(24vw - 15px)'};
  };
  @media (min-width: 896px) and (max-width: 1199px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(24vw - 15px)'};
    max-height: 300px;
  };
  @media (min-width: 1200px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(16vw - 15px)'};
    max-height: 277px;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${(props: { height?: number | string, maxHeight?: string | null}) => props.height || 'calc(50vw - 15px)'};
  max-height: ${(props: {height?: number | string, maxHeight?: string | null}) => props.maxHeight || 'unset'};
  @media (min-width: 700px) and (max-width: 895px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(24vw - 15px)'};
  };
  @media (min-width: 896px) and (max-width: 1199px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(24vw - 15px)'};
    max-height: 300px;
  };
  @media (min-width: 1200px) {
    height: ${(props: { height?: number | string }) => props.height || 'calc(16vw - 15px)'};
    max-height: 277px;
  }
`;

const loadingAnimation = keyframes`
  0% {
    background-color: #fff;
  }
  50% {
    background-color: #eeeeee;
  }
  100% {
    background-color: #fff;
  }
`;

const Placeholder = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  animation: ${loadingAnimation} 2s infinite;
`;

interface MediaProps {
    media: MediaItem[] | null,
    type: MediaType,
    height?: number | string,
    disableLazy?: boolean,
    asCarousel?: boolean,
    maxHeight?: string | null,
    /** Разрешает движение: гифка анимируется, видео играет. Только в открытой карточке. */
    playable?: boolean
}


type Props = MediaProps;

// Заглушка «нет фото» — обычный элемент медиа, чтобы у неё был тот же путь рендера,
// что и у настоящих: пустые preview/regular всегда сводятся к source.
const NO_MEDIA: MediaItem = {
    preview: '',
    previewHighRes: '',
    regular: '',
    regularHighRes: '',
    source: noImage,
    kind: MediaKind.image,
};

interface LazyFrameProps {
    media: MediaItem,
    type: MediaType,
    playable: boolean
}

/*
 * Кадр с собственным плейсхолдером.
 *
 * Плейсхолдер обязан быть состоянием этого компонента, а не общим ref'ом снаружи:
 * при общем ref'е React оставляет в нём узел последнего смонтированного слайда, и
 * догрузившаяся картинка снимала мерцающий прямоугольник с чужого слайда, а свой
 * оставляла поверх готового изображения.
 *
 * Отложенную загрузку делает сам браузер (`loading="lazy"`), а не react-lazyload:
 * та проверяла видимость только при монтировании и по scroll/resize. Слайд монтируется
 * раньше, чем догрузится обложка меню и сдвинет раскладку, — проверка не проходила, а
 * без скролла гостя больше не повторялась, и картинка не появлялась вообще.
 */
const LazyFrame = ({media, type, playable}: LazyFrameProps) => {
    const [ready, setReady] = useState(false);
    const onReady = () => setReady(true);

    return (
        <>
            <MediaFrame
                media={media}
                type={type}
                playable={playable}
                variant="fill"
                lazy={true}
                onReady={onReady}
            />
            {!ready && <Placeholder/>}
        </>
    );
};

const LazyImage = (props: Props) => {
    const playable = props.playable === true;
    const mediaList = props.media && props.media.length > 0 ? props.media : [NO_MEDIA];

    // disableLazy — просьба вызывающего показать кадр сразу (корзина, меню категорий).
    // Плейсхолдер тут не нужен: ждать нечего.
    const renderInlineFrame = (mediaItem: MediaItem) => (
        <MediaFrame
            media={mediaItem}
            type={props.type}
            playable={playable}
            variant="inline"
            lazy={false}
        />
    );

    const renderFrame = (mediaItem: MediaItem) =>
        props.disableLazy ? renderInlineFrame(mediaItem) : (
            <LazyFrame media={mediaItem} type={props.type} playable={playable}/>
        );

    if (props.asCarousel && mediaList.length > 1) {
        return (
            <CarouselWrapper height={props.height}>
                <AwesomeSlider
                    organicArrows={false}
                    className={props.type == MediaType.preview ? 'awssldPreview' : 'awssldRegular'}
                >
                    {/*
                      * AwesomeSlider не рендерит своих детей: transformChildren берёт у элемента
                      * только props и выбрасывает сам элемент. Поэтому ребёнком обязан быть
                      * простой элемент, несущий контент в children, — компонент сюда положить
                      * нельзя, его пропсы просто осядут атрибутами на пустом awssld__content.
                      * Сам этот div в DOM не попадёт.
                      */}
                    {mediaList.map((mediaItem, mediaIndex) => (
                        <div key={mediaIndex}>{renderFrame(mediaItem)}</div>
                    ))}
                </AwesomeSlider>
            </CarouselWrapper>
        );
    }

    return props.disableLazy ? renderInlineFrame(mediaList[0]) : (
        <ImageWrapper height={props.height} maxHeight={props.maxHeight}>
            {renderFrame(mediaList[0])}
        </ImageWrapper>
    );
};


export default LazyImage;


